import { Button, Card, Row, Space, Divider, Col } from 'antd';
import { HolderOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useEffect, useState } from 'react';

type SortableItemProps = {
  value: string;
  id: string;
  index: number;
  onRemove: () => void;
  style: React.CSSProperties;
};

type SortableListProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  optionsMap: Record<string, any>;
  style: React.CSSProperties;
  itemsHeader?: string;
  exculdedItemsHeader?: string;
  emptyItemsText?: string;
};

const SortableItem = ({ value, id, index, onRemove, style }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const itemStyle = {
    ...style,
    cursor: 'pointer',
    marginBottom: '10px',
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card ref={setNodeRef} style={itemStyle} bodyStyle={{ padding: '5px', verticalAlign: 'middle' }}>
      <Row align="middle" wrap={false}>
        <Col flex="auto" {...attributes} {...listeners}>
          <Space size={4}>
            <HolderOutlined />
            <b>
              {index + 1} {value}
            </b>
          </Space>
        </Col>
        <Col>
          <Button shape="circle" onClick={onRemove} icon={<CloseOutlined />} />
        </Col>
      </Row>
    </Card>
  );
};

const remove = (index: number, items: string[]) => {
  const newItems = [...items];
  newItems.splice(index, 1);
  return newItems;
};

const add = (key: string, items: string[]) => {
  const newItems = [...items];
  newItems.push(key);
  return newItems;
};
export const SortableList = ({
  value,
  itemsHeader = '',
  exculdedItemsHeader = '',
  emptyItemsText = '',
  onChange,
  optionsMap,
  style,
}: SortableListProps) => {
  const [items, setItems] = useState<string[]>([]);

  useEffect(() => {
    if (value) {
      setItems(value);
    }
  }, [value]);

  const updateItems = (newItems: string[]) => {
    setItems(newItems);
    onChange?.(newItems);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string);
      const newIndex = items.indexOf(over.id as string);
      updateItems(arrayMove(items, oldIndex, newIndex));
    }
  };

  const exculdedItems = Object.keys(optionsMap).filter((key) => !items.includes(key));

  return (
    <div style={{ width: style.width }}>
      {items && items.length > 0 ? (
        <>
          {itemsHeader}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
              {items.map((key, index) => (
                <SortableItem
                  key={`item-${key}`}
                  id={key}
                  index={index}
                  value={optionsMap[key]}
                  onRemove={() => updateItems(remove(index, items))}
                  style={style}
                />
              ))}
            </SortableContext>
          </DndContext>
        </>
      ) : (
        <b>{emptyItemsText}</b>
      )}

      {exculdedItems && exculdedItems.length > 0 && (
        <>
          <Divider plain />
          {exculdedItemsHeader}
          {exculdedItems.map((key) => (
            <Card
              key={key}
              style={{ ...style, marginBottom: '10px' }}
              bodyStyle={{ padding: '5px', marginLeft: '14px' }}
            >
              <Row justify="space-between" align="middle">
                <b>
                  <p style={{ color: 'grey', margin: '0' }}>{optionsMap[key]}</p>
                </b>
                <Button shape="circle" onClick={() => updateItems(add(key, items))} icon={<PlusOutlined />} />
              </Row>
            </Card>
          ))}
        </>
      )}
    </div>
  );
};
